"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight, Mail, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type SendCodeResponse = {
  ok: boolean;
  data?: { devCode?: string; expiresInSeconds: number };
  error?: { message: string };
};

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string | undefined>();
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  async function sendCode() {
    setIsSending(true);
    setMessage("");
    const response = await fetch("/api/auth/send-code", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const result = (await response.json()) as SendCodeResponse;
    setIsSending(false);

    if (!result.ok) {
      setMessage(result.error?.message || "验证码发送失败。");
      return;
    }

    setDevCode(result.data?.devCode);
    setMessage("验证码已生成。请在有效期内完成登录。");
  }

  async function verifyCode() {
    setIsVerifying(true);
    setMessage("");
    const response = await fetch("/api/auth/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code }),
    });
    const result = (await response.json()) as { ok: boolean; error?: { message: string } };
    setIsVerifying(false);

    if (!result.ok) {
      setMessage(result.error?.message || "登录失败。");
      return;
    }

    router.push("/reports/new");
  }

  return (
    <main className="min-h-screen bg-background">
      <div className="container grid min-h-screen gap-8 py-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <section>
          <p className="mb-4 inline-flex rounded-sm border border-primary/20 bg-accent px-3 py-1 text-sm font-medium text-primary">
            安全登录
          </p>
          <h1 className="text-4xl font-semibold leading-tight text-slate-950">
            使用邮箱验证码进入遗产线索查询
          </h1>
          <p className="mt-5 max-w-xl text-base leading-8 text-slate-600">
            平台采用无密码邮箱验证码登录。报告、上传记录和问答进度会归属于当前邮箱账号。
          </p>
          <div className="mt-8 grid gap-4 text-sm text-muted-foreground">
            <div className="flex gap-3">
              <ShieldCheck className="mt-0.5 size-5 text-secondary" />
              <span>报告和上传文件默认仅当前登录用户可见。</span>
            </div>
            <div className="flex gap-3">
              <Mail className="mt-0.5 size-5 text-secondary" />
              <span>邮箱将作为报告归属和后续通知的主要凭据。</span>
            </div>
          </div>
        </section>

        <Card className="shadow-subtle">
          <CardHeader>
            <CardTitle>登录/注册</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <label className="block space-y-2">
              <span className="text-sm font-medium">邮箱地址</span>
              <input
                className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                placeholder="example@email.com"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <Button className="w-full" onClick={sendCode} disabled={isSending}>
              {isSending ? "发送中..." : "发送验证码"}
            </Button>

            {devCode ? (
              <div className="rounded-md border border-primary/20 bg-accent p-3 text-sm text-primary">
                本地测试验证码：<strong>{devCode}</strong>
              </div>
            ) : null}

            <label className="block space-y-2">
              <span className="text-sm font-medium">6位验证码</span>
              <input
                className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm tracking-[0.4em] outline-none focus:ring-2 focus:ring-ring"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </label>
            <Button className="w-full" onClick={verifyCode} disabled={isVerifying}>
              {isVerifying ? "验证中..." : "登录并开始"}
              <ArrowRight className="ml-2 size-4" />
            </Button>

            {message ? (
              <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                {message}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
